import useFilter, { PLATFORM_OPTION } from '@composables/useFilter.js'
import { getCurrentInstance, computed, isRef, watch, ref } from 'vue'
import { platformParam, sdkPlatform, HEADERS } from '@config/game.js'
import { ADMIN_FILTER_OPTION } from '@composables/useFilter.js'
import { PAGINATION_DEFAULT_PAGE } from '@config/options.js'
import usePagination from '@composables/usePagination.js'
import { GUIDE_PINNED_TAG } from '@config/guides.js'
import useSearch from '@composables/useSearch.js'
import useRoute from '@composables/useRoute.js'
import useQuery from '@composables/useQuery.js'
import useSort from '@composables/useSort.js'
import { API_RESULT_LIMIT } from '@config'
import {
  getQueryFromPath,
  isNumber,
  isArray,
  isEqual,
  clone,
} from '@helpers/utils.js'

let lastBrowseState = {}
export const globalUpdateFns = {}

export default function ({
  id,
  fetch,
  items,
  total,
  simple,
  sortOptions,
  browsePage,
  initialSort,
  infinite = false,
  limit,
  refresh,
  isGuides,
} = {}) {
  const previousFilters = ref({ selected: [], excluded: [] })

  const { emit } = getCurrentInstance()

  const events = ['fetch:paginate-next', 'fetch:paginate-prev', 'fetch:query']

  if (!id || !isRef(items) || !isRef(total) || !sortOptions || !isRef(limit)) {
    throw new TypeError()
  }

  const { routeQuery: query, replace } = useRoute()
  const validStates = ['invalid', 'prev', 'next']

  const rangeRef = ref({ start: 0, end: 0 })
  const stateRef = ref(null)
  const itemsRef = ref([])
  const inMemTotal = ref(null)

  const itemList = computed(() => itemsRef.value || [])
  const state = computed(() => stateRef.value)
  const range = computed(() => rangeRef.value)

  const allResultsRetrieved = computed(
    () => isArray(items.value?.data) && items.value.data.length === total.value
  )

  const canShowMore = computed(() => {
    const maxItemLimit = Math.max(itemList.value.length, pagination.value.limit)

    if (inMemTotal.value < API_RESULT_LIMIT) {
      return inMemTotal.value > maxItemLimit
    }
    return total.value > maxItemLimit
  })

  const { queryObject, getStrippedQuery } = useQuery(id)
  const { setSort, sort, getDefaultSort, inMemorySort } = useSort(
    id,
    query.value._sort,
    sortOptions
  )

  const {
    activeSelectedFilters,
    activeExcludedFilters,
    activeFiltersCount,
    inMemoryFilter,
  } = useFilter(id)

  const { inMemorySearch } = useSearch(
    id,
    query.value._q,
    query.value['submitted_by_display_name-lk']?.replace('*', '')
  )

  const {
    inMemoryPagination,
    resetPagination,
    calculateOffset,
    setPagination,
    pagination,
    getLimit,
  } = usePagination(id, {
    limit: limit.value,
    offset: 0,
    page: 1,
    simple,
  })

  if (!sort.value) {
    const defaultSort = getDefaultSort()

    if (defaultSort) {
      updateSort({ ...defaultSort }, true)
    }
  }

  watch(itemsRef, () => {
    setValid()
    if (refresh) {
      const _query = getStrippedQuery(getQuery(), getDefaultSort(), true)
      if (!isEqual(query.value, _query)) replace({ query: _query })
    }
  })

  watch(
    [items, total],
    ([_items, _total], [, _oldTotal]) => {
      if (!isArray(_items?.data)) return

      _setRange(_items.data.length)

      // TODO:  Being used only by **game** browse to resolve the inconsistency with sorting by downloads, popular
      // and any cron dependent fields. Can remove once API provides consistent sorting option by popularity.
      if (initialSort?.value) {
        updateTable('sort', { ...getDefaultSort(), dropdown: true })
        return
      }

      if (
        lastBrowseState.id === id &&
        !_oldTotal &&
        getLimit.value < lastBrowseState.limit
      ) {
        setPagination({ limit: lastBrowseState.limit })
      }

      inMemTotal.value = simple ? _items.data.length : 0
      if (simple) {
        setPagination({ total: _items.data.length })
      } else if (isNumber(_total) && _total !== _oldTotal) {
        setPagination({ total: _total, page: PAGINATION_DEFAULT_PAGE })
      }

      if (isValid()) {
        updateTable()
      } else {
        // If invalid then we must overwrite items and start at offset 0
        // pagination and sort will be correctly configured before the fetch event
        _updateItems(0)
      }
    },
    { immediate: true }
  )

  if (simple) {
    watch(limit, (_limit) => {
      setPagination({ limit: _limit })
    })

    watch(
      () => items.value?.nextPageUrl,
      (_nextPageUrl) => {
        setPagination({ hasNextPage: !!_nextPageUrl })
      }
    )
  }

  function hadFiltered(type) {
    // Fetch for items if type is search and there was a
    // previous search. Cannot do in-memory in this case
    // because result_total might not contain all results
    if (type === 'search') {
      return (
        items.value?.query?._q ||
        items.value?.query?.submitted_by ||
        items.value?.query?.['submitted_by_display_name-lk']
      )
    }

    return false
  }

  function updateTable(type, data) {
    if (type === 'sort') {
      updateSort(data)
    }

    if (browsePage && hadFiltered(type)) {
      setInvalid()
      if (!fetch.value) {
        inMemoryUpdate()
        return
      }
    }

    const _query = !simple ? getQuery() : {}

    if (!platformParam && !sdkPlatform.value) {
      if (!_query.platforms && !!HEADERS.platform) {
        HEADERS.platform = null
      } else if (_query.platforms && !HEADERS.platform) {
        HEADERS.platform = _query.platforms
      }
    }

    // If all results are available operate inmemory
    if (
      (allResultsRetrieved.value && isValid()) ||
      (isEqual(
        { ..._query, _offset: 0 },
        { ...items.value?.query, _offset: 0 }
      ) &&
        type !== 'pagination')
    ) {
      inMemoryUpdate()
      // set the correct pagination
      if (type === 'filter' && activeFiltersCount.value) {
        setPagination({ total: itemList.value.length })
      }
      return
    }

    // If execution reach this point inmemory update was not possible
    // we will emit query if type exist and is not pagination
    if ((type && type !== 'pagination') || !isValid()) {
      if (fetch.value) {
        if (type !== 'sort' || infinite) {
          resetPagination()
          inMemTotal.value = 0
        }
        setInvalid()
        // Can't reuse _query here
        emit('fetch:query', getQuery())
      } else {
        emit('fetch:query', queryObject.value)
      }

      // clear items to remove from infinte ui when fetching query
      if (infinite) {
        itemsRef.value = []
      }
      if (!initialSort?.value) {
        return
      }
    }

    const currentOffset = calculateOffset()
    // Don't exceed end of result total
    let endOffset = currentOffset + getLimit.value
    endOffset = (simple ? endOffset : Math.min(endOffset, total.value)) - 1

    if (type === 'pagination') {
      const hasPassEnd = endOffset > range.value.end
      const hasPassStart = currentOffset < range.value.start
      if (hasPassEnd) {
        if (!simple || items.value.nextPageUrl) {
          setState('next')
          emit('fetch:paginate-next', getQuery('next'))
        }
        return
      } else if (hasPassStart) {
        setState('prev')
        emit('fetch:paginate-prev', getQuery('prev'))
        return
      } else if (data === 'limit') {
        setInvalid()
        emit('fetch:query', getQuery())
        return
      }
      setInvalid()
      // pagination can fall through and do inmemory paginate
    }

    let start = currentOffset - (isInitialFetch() ? 1 : range.value.start)

    _updateItems(start)
  }
  globalUpdateFns[id] = updateTable

  function updateSort(_sort, defaultDirection = false) {
    if (_sort.dropdown) {
      _setSort(_sort, _sort.direction)
      return
    }

    _sort.key === sort.value?.sortBy && !defaultDirection
      ? _setSort(_sort, _reverseOrder())
      : _setSort(_sort, _sort.direction)
  }

  function _reverseOrder() {
    return sort.value?.order === 'asc' ? 'desc' : 'asc'
  }

  function _setSort(_sort, order) {
    setSort({
      ..._sort,
      sortBy: _sort.key,
      order,
    })
  }

  function setState(state) {
    if (validStates.includes(state)) {
      stateRef.value = state
    }
  }

  function isInitialFetch() {
    return range.value.end === 0
  }

  function setInvalid() {
    stateRef.value = 'invalid'
  }

  function isValid() {
    return stateRef.value !== 'invalid'
  }

  function setValid() {
    stateRef.value = null
  }

  function hasTagFromSelectedDropdown(newFilter) {
    return (
      newFilter?.type === 'dropdown' &&
      previousFilters.value.selected.some((f) => f.id === newFilter.id)
    )
  }

  function getNewSelectedFilter() {
    let newFilter
    activeSelectedFilters.value.forEach((f) => {
      if (
        previousFilters.value.selected.some(
          (p) => p.id === f.id && p.tag === f.tag && p.type === f.type
        )
      ) {
        return
      }

      newFilter = f
    })

    return newFilter
  }

  // Determines if a fetch is required
  function handleFilterUpdate() {
    const newFilters = {
      selected: activeSelectedFilters.value,
      excluded: activeExcludedFilters.value,
    }

    const newSelectedFilter = getNewSelectedFilter()

    const previouslyFiltered =
      items.value?.query?.['collection_tags-not-in'] ||
      items.value?.query?.collection_tags ||
      items.value?.query?.['tags-not-in'] ||
      items.value?.query?.content_type ||
      items.value?.query?.date_live ||
      items.value?.query?.platforms ||
      items.value?.query?.timespan ||
      items.value?.query?.admin ||
      items.value?.query?.tags ||
      items.value?.query?._q ||
      items.value?.query?.id

    const invalid =
      // Invalidate if changing filter from same dropdown and had previously filtered
      (previouslyFiltered && hasTagFromSelectedDropdown(newSelectedFilter)) ||
      // Always invalidate if filtering by status/visibility or platform
      newSelectedFilter?.filter === ADMIN_FILTER_OPTION ||
      newSelectedFilter?.filter === PLATFORM_OPTION ||
      // initial load with filters makes cache invalid
      (((previousFilters.value.selected.length === 0 &&
        (activeSelectedFilters.value.length !== newFilters.selected.length ||
          activeExcludedFilters.value.length !== newFilters.excluded.length)) ||
        // filters deselected makes cache invalid
        previousFilters.value.selected.length > newFilters.selected.length ||
        previousFilters.value.excluded.length > newFilters.excluded.length) &&
        (!allResultsRetrieved.value || previouslyFiltered))

    if (invalid) setInvalid()

    previousFilters.value = newFilters
  }

  // Range is indexed at 0
  function _setRange(count) {
    let start = range.value.start
    let end = range.value.end

    if (!isValid()) {
      start = Math.max(0, queryObject.value._offset - 1)
      end = start ? start + count : start + count - 1
      if (queryObject.value._offset !== 0) {
        start++
        end++
      }
    } else if (state.value === 'next') {
      end = range.value.end + (simple ? limit.value : API_RESULT_LIMIT)
    } else if (state.value === 'prev') {
      start = Math.max(0, start - (simple ? limit.value : API_RESULT_LIMIT))
    } else {
      end = count - 1
    }

    rangeRef.value = {
      start,
      end: simple ? end : Math.min(end, total.value - 1),
    }
  }

  // TODO: possible to fetch limit num records from the end
  // to pre fetch previous items and save in memory
  function getQuery(direction) {
    const query = { ...queryObject.value, _limit: API_RESULT_LIMIT }
    // if start offset is less than limit and paginating to prev
    // we can set offset to 0 and pre fetch to the start of the result set
    if (simple) {
      return getQueryFromPath(items.value.nextPageUrl)
    } else if (direction === 'prev') {
      if (range.value.start <= API_RESULT_LIMIT) {
        query._limit = range.value.start
        query._offset = 0
      } else {
        query._offset = range.value.start - API_RESULT_LIMIT
      }
    } else if (isValid()) {
      const offset =
        range.value?.end === total.value
          ? calculateOffset()
          : !range.value.start && !isInitialFetch()
            ? range.value.end + 1
            : range.value.end
      query._offset = Math.max(0, offset)
    }

    const strip = [
      'tags',
      'tags-not-in',
      'platforms',
      '_q',
      'submitted_by',
      'submitted_by_display_name-lk',
    ]
    for (const param of strip) {
      if (!query[param]) {
        delete query[param]
      }
    }

    return query
  }

  function inMemoryUpdate() {
    const _items = clone(items.value.data)
    let data = inMemorySort({ data: _items }, false)

    data = inMemoryFilter(data, false)
    data = inMemorySearch(data, isGuides)
    if (isGuides) data = _sortPinnedGuides(data)

    inMemTotal.value = data.length
    if (infinite) {
      itemsRef.value = data.slice(
        0,
        Math.min(total.value, pagination.value.limit)
      )
    } else {
      data = inMemoryPagination(data, false)
      itemsRef.value = data
    }
  }

  function _updateItems(start) {
    const _start = infinite ? 0 : Math.max(0, start)
    let data = items.value?.data || []
    if (isGuides) data = _sortPinnedGuides(data)

    itemsRef.value = data.slice(_start, _start + getLimit.value)

    lastBrowseState = {
      id,
      limit: getLimit.value,
    }
  }

  function _sortPinnedGuides(items) {
    const pinned =
      items
        .filter((article) =>
          article.tags.find(
            (tag) => tag.name.toUpperCase() === GUIDE_PINNED_TAG
          )
        )
        .toSorted((a, b) => a.date_added - b.date_added) || []

    const unpinned = items.filter(
      (article) => !pinned.find((pin) => article.id === pin.id)
    )

    return [...pinned, ...unpinned]
  }

  function handleScroll() {
    setPagination({ limit: pagination.value.limit + limit.value })
    updateTable('pagination', 'next')
  }

  return {
    allResultsRetrieved,
    handleFilterUpdate,
    handleScroll,
    canShowMore,
    updateTable,
    setInvalid,
    setValid,
    itemList,
    setState,
    events,
    range,
    state,
    sort,
  }
}
